import React, { useCallback, useEffect, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { fetchRestaurantMealSpaceCandidates } from '@/apis/aipass'
import { useErrorHandler } from '@/hooks/use-error-handler'
import type { RestaurantMealSpaceCandidateResponse, RestaurantSpaceReservationDetailView } from '@/models/restaurant-manager'
import { formatMealSpaceSelectionLabel } from '@/models/restaurant-manager'
import { RestaurantMealSpaceCandidateSelect } from './restaurant-meal-space-candidate-select'
import { RestaurantEditableCellShell } from './restaurant-editable-cell-shell'
import { restaurantFieldHighlightStyle } from './restaurant-table-styles'
import { useRestaurantMealSpaceDraft } from './use-restaurant-meal-space-draft'
import { useRestaurantMealSpaceSave } from './use-restaurant-meal-space-save'
import { useRestaurantInlineEditor } from './use-restaurant-inline-editor'

type Props = {
  reservationId: string
  date: string
  details: RestaurantSpaceReservationDetailView[]
}

export const RestaurantMealSpaceCell: React.FC<Props> = ({ reservationId, date, details }) => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()

  const [isEditing, setIsEditing] = useState(false)
  const [openDropdownRowId, setOpenDropdownRowId] = useState<string | null>(null)
  const [candidateResponse, setCandidateResponse] = useState<RestaurantMealSpaceCandidateResponse | null>(null)
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(false)

  const draft = useRestaurantMealSpaceDraft(details)
  const { isSaving, saveRowSelection, deleteRow } = useRestaurantMealSpaceSave({
    reservationId,
    getRow: draft.getRow,
    getCurrentRows: draft.getCurrentRows,
    applySelection: draft.applySelection,
    replaceDraftRows: draft.replaceDraftRows,
    removeRowById: draft.removeRowById,
    setOpenDropdownRowId,
    setCandidateResponse,
  })

  useEffect(() => {
    setCandidateResponse(null)
    setOpenDropdownRowId(null)
    setIsEditing(false)
  }, [reservationId, date])

  const ensureCandidateResponse = useCallback(async () => {
    try {
      setIsLoadingCandidates(true)
      const response = await fetchRestaurantMealSpaceCandidates({ reservationId, date })
      setCandidateResponse(response)
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoadingCandidates(false)
    }
  }, [candidateResponse, date, errorHandler, reservationId])

  const closeEditor = useCallback(() => {
    if (isSaving) {
      return
    }

    setOpenDropdownRowId(null)
    setIsEditing(false)
    draft.resetToSaved()
  }, [draft, isSaving])

  const { containerRef, handleCompositionEnd, handleCompositionStart, handleEditorKeyDown } = useRestaurantInlineEditor({
    isEditing,
    isBusy: isSaving,
    onRequestClose: closeEditor,
    canCloseOnEnter: () => openDropdownRowId == null,
    isInsideIgnoredElement: target => target instanceof Element && target.closest('.meal-space-select-popup') != null,
  })

  useEffect(() => {
    if (isEditing) {
      containerRef.current?.focus()
    }
  }, [containerRef, isEditing])

  const startEdit = useCallback(() => {
    if (isSaving) {
      return
    }

    setOpenDropdownRowId(null)
    setIsEditing(true)
    draft.replaceDraftRows(draft.buildInitialRows())
  }, [draft, isSaving])

  const startAdd = useCallback(() => {
    if (isSaving || draft.hasUnsavedRow) {
      return
    }

    draft.addEmptyRow()
    setOpenDropdownRowId(null)
  }, [draft, isSaving])

  return (
    <div
      ref={containerRef}
      css={cellContainerStyle}
      tabIndex={isEditing ? 0 : -1}
      onCompositionStart={handleCompositionStart}
      onCompositionEnd={handleCompositionEnd}
      onKeyDown={isEditing ? handleEditorKeyDown : undefined}
    >
      <RestaurantEditableCellShell canEdit isEditing={isEditing} onStartEdit={startEdit} openOnClick editButtonAriaLabel={t('Edit')}>
        {isEditing ? (
          <div css={editorContentStyle}>
            <div css={selectedListStyle}>
              {draft.draftRows.map(row => (
                <div key={row.localRowId} css={editRowStyle}>
                  <RestaurantMealSpaceCandidateSelect
                    value={row.currentSelection}
                    candidateSpaces={candidateResponse?.candidateSpaces ?? []}
                    isDropdownOpen={openDropdownRowId === row.localRowId}
                    isLoading={isLoadingCandidates}
                    disabled={isSaving}
                    onOpenChange={open => {
                      if (open) {
                        setOpenDropdownRowId(row.localRowId)
                        ensureCandidateResponse()
                      } else {
                        setOpenDropdownRowId(null)
                      }
                    }}
                    onChange={selection => void saveRowSelection(row.localRowId, selection)}
                  />
                  <button
                    type="button"
                    css={deleteButtonStyle}
                    onClick={() => void deleteRow(row, t('Are you sure you want to delete it'))}
                    disabled={isSaving}
                    aria-label={t('Delete')}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            {!draft.hasUnsavedRow && (
              <button type="button" css={addButtonStyle} onClick={startAdd} disabled={isSaving}>
                + {t('RestaurantManager.Add')}
              </button>
            )}
          </div>
        ) : (
          <ReadonlyContent details={details} />
        )}
      </RestaurantEditableCellShell>
    </div>
  )
}

const ReadonlyContent: React.FC<{ details: RestaurantSpaceReservationDetailView[] }> = ({ details }) => {
  if (details.length === 0) {
    return (
      <div css={readonlyListStyle}>
        <div css={readonlyPlaceholderStyle}>-</div>
      </div>
    )
  }

  return (
    <div css={readonlyListStyle}>
      {details.map(detail => (
        <div key={detail.spaceUsageDetailId} css={[readonlyLineStyle, detail.isHighlighted && restaurantFieldHighlightStyle]}>
          {formatMealSpaceSelectionLabel(detail.spaceName, detail)}
        </div>
      ))}
    </div>
  )
}

const cellContainerStyle = css({
  minWidth: 0,
  outline: 'none',
  width: '100%',
})

const editorContentStyle = css({
  width: '100%',
})

const selectedListStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
})

const editRowStyle = css({
  alignItems: 'center',
  display: 'flex',
  gap: 8,
  justifyContent: 'flex-start',
  width: '100%',
})

const deleteButtonStyle = css({
  alignItems: 'center',
  background: 'transparent',
  border: 'none',
  color: '#F2A40B',
  cursor: 'pointer',
  display: 'inline-flex',
  fontSize: 24,
  height: 24,
  justifyContent: 'center',
  lineHeight: 1,
  padding: 0,
  width: 24,
})

const addButtonStyle = css({
  alignSelf: 'flex-start',
  background: 'transparent',
  border: 'none',
  color: '#F2A40B',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 'bold',
  marginTop: 8,
  padding: 0,
})

const readonlyListStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  minHeight: 20,
  width: '100%',
})

const readonlyLineStyle = css({
  fontSize: 14,
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
})

const readonlyPlaceholderStyle = css({
  color: '#999999',
  fontSize: 12,
  lineHeight: 1.5,
})
